import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { useParams } from "react-router-dom";
import { integrationsApi } from "../../api/integrations";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useProjectSettingsDirty } from "../../components/dashboard/ProjectSettingsUnsavedContext";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const emptyIntegration = {
  enabled: true,
  environment: "prod",
  cuit: "",
  legalName: "",
  pointOfSale: "",
  validationStatus: "not_configured",
  hasCertificate: false,
  hasPrivateKey: false,
  certificate: {},
  lastValidatedAt: null,
  lastError: "",
};

function formatDate(value, fallback = "—") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(date);
}

function digits(value, max) {
  return String(value || "").replace(/\D+/g, "").slice(0, max);
}

function statusMeta(integration) {
  if (integration.validationStatus === "valid") {
    return { label: "Credenciales listas", color: "success", icon: <CheckCircleRoundedIcon /> };
  }
  if (integration.validationStatus === "error") {
    return { label: "Revisar credenciales", color: "error", icon: <VerifiedUserRoundedIcon /> };
  }
  if (integration.hasCertificate || integration.hasPrivateKey || integration.cuit) {
    return { label: "Configuración incompleta", color: "warning", icon: <VerifiedUserRoundedIcon /> };
  }
  return { label: "Sin configurar", color: "default", icon: <VerifiedUserRoundedIcon /> };
}

export default function ProjectFiscalBillingPage() {
  const { projectId } = useParams();
  const [integration, setIntegration] = React.useState(emptyIntegration);
  const [form, setForm] = React.useState({
    enabled: true,
    environment: "prod",
    cuit: "",
    legalName: "",
    pointOfSale: "",
    certificatePem: "",
    privateKeyPem: "",
  });
  const [certificateFileName, setCertificateFileName] = React.useState("");
  const [privateKeyFileName, setPrivateKeyFileName] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const applyIntegration = React.useCallback((nextIntegration = {}) => {
    const next = { ...emptyIntegration, ...nextIntegration };
    setIntegration(next);
    setForm({
      enabled: next.enabled !== false,
      environment: next.environment || "prod",
      cuit: next.cuit || "",
      legalName: next.legalName || "",
      pointOfSale: next.pointOfSale ? String(next.pointOfSale) : "",
      certificatePem: "",
      privateKeyPem: "",
    });
    setCertificateFileName("");
    setPrivateKeyFileName("");
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await integrationsApi.getArca(projectId);
      applyIntegration(data.integration || emptyIntegration);
    } catch (err) {
      setError(err.message || "No se pudo cargar la facturación ARCA.");
    } finally {
      setLoading(false);
    }
  }, [projectId, applyIntegration]);

  React.useEffect(() => {
    load();
  }, [load]);

  const dirty = React.useMemo(() => {
    if (loading) return false;
    return Boolean(
      form.enabled !== (integration.enabled !== false)
      || form.environment !== (integration.environment || "prod")
      || form.cuit !== (integration.cuit || "")
      || form.legalName !== (integration.legalName || "")
      || String(form.pointOfSale || "") !== String(integration.pointOfSale || "")
      || form.certificatePem
      || form.privateKeyPem
    );
  }, [loading, form, integration]);
  useProjectSettingsDirty("arca-billing", dirty);

  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const readCredentialFile = async (event, field, setName) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      change(field, text);
      setName(file.name || "Archivo cargado");
      setError("");
    } catch {
      setError("No se pudo leer el archivo seleccionado.");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        enabled: form.enabled,
        environment: form.environment,
        cuit: form.cuit,
        legalName: form.legalName.trim(),
        pointOfSale: Number(form.pointOfSale || 0),
      };
      if (form.certificatePem.trim()) payload.certificatePem = form.certificatePem;
      if (form.privateKeyPem.trim()) payload.privateKeyPem = form.privateKeyPem;
      const data = await integrationsApi.saveArca(projectId, payload);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Configuración fiscal guardada.");
    } catch (err) {
      if (err?.data?.integration) applyIntegration(err.data.integration);
      setError(err.message || "No se pudo guardar la configuración fiscal.");
    } finally {
      setSaving(false);
    }
  };

  const validateStored = async () => {
    setTesting(true);
    setError("");
    setMessage("");
    try {
      const data = await integrationsApi.testArca(projectId);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Credenciales validadas.");
    } catch (err) {
      if (err?.data?.integration) applyIntegration(err.data.integration);
      setError(err.message || "No se pudieron validar las credenciales.");
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar las credenciales fiscales de este proyecto?")) return;
    setDeleting(true);
    setError("");
    setMessage("");
    try {
      const data = await integrationsApi.deleteArca(projectId);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Credenciales eliminadas.");
    } catch (err) {
      setError(err.message || "No se pudieron eliminar las credenciales.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <DashboardLoadingState label="Cargando facturación..." />;

  const status = statusMeta(integration);
  const hasStoredCredentials = integration.hasCertificate || integration.hasPrivateKey;

  return (
    <Stack spacing={2.2} component="form" onSubmit={save}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Facturación ARCA
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35 }}>
            Identidad fiscal y credenciales del proyecto.
          </Typography>
        </Box>
        <Chip icon={status.icon} label={status.label} color={status.color} variant="outlined" sx={{ alignSelf: "flex-start", fontWeight: 900 }} />
      </Stack>

      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={1.7}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Datos fiscales</Typography>
                  <Typography variant="caption" color="text.secondary">Lo mínimo para identificar al emisor.</Typography>
                </Box>
                <Switch checked={form.enabled} onChange={(event) => change("enabled", event.target.checked)} inputProps={{ "aria-label": "Facturación habilitada" }} />
              </Stack>

              <TextField
                autoFocus
                label="CUIT"
                value={form.cuit}
                onChange={(event) => change("cuit", digits(event.target.value, 11))}
                inputProps={{ inputMode: "numeric", maxLength: 11 }}
                fullWidth
                required
              />
              <TextField
                label="Razón social"
                value={form.legalName}
                onChange={(event) => change("legalName", event.target.value)}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <TextField
                  label="Punto de venta"
                  value={form.pointOfSale}
                  onChange={(event) => change("pointOfSale", digits(event.target.value, 5))}
                  inputProps={{ inputMode: "numeric", maxLength: 5 }}
                  fullWidth
                  required
                />
                <TextField
                  select
                  label="Ambiente"
                  value={form.environment}
                  onChange={(event) => change("environment", event.target.value)}
                  fullWidth
                >
                  <MenuItem value="prod">Producción</MenuItem>
                  <MenuItem value="homo">Homologación</MenuItem>
                </TextField>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={1.7}>
              <Box>
                <Typography sx={{ fontWeight: 950 }}>Certificado ARCA</Typography>
                <Typography variant="caption" color="text.secondary">Se cifra antes de guardarlo. Nunca vuelve al navegador.</Typography>
              </Box>

              <Button component="label" variant={integration.hasCertificate || certificateFileName ? "outlined" : "contained"} startIcon={<UploadFileRoundedIcon />} sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 850 }}>
                {certificateFileName || (integration.hasCertificate ? "Reemplazar certificado" : "Cargar certificado .crt / .pem")}
                <input hidden type="file" accept=".crt,.cer,.pem,text/plain" onChange={(event) => readCredentialFile(event, "certificatePem", setCertificateFileName)} />
              </Button>

              <Button component="label" variant={integration.hasPrivateKey || privateKeyFileName ? "outlined" : "contained"} startIcon={<KeyRoundedIcon />} sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 850 }}>
                {privateKeyFileName || (integration.hasPrivateKey ? "Reemplazar clave privada" : "Cargar clave privada .key / .pem")}
                <input hidden type="file" accept=".key,.pem,text/plain" onChange={(event) => readCredentialFile(event, "privateKeyPem", setPrivateKeyFileName)} />
              </Button>

              {integration.certificate?.validTo ? (
                <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 900 }}>
                    Certificado vigente hasta {formatDate(integration.certificate.validTo)}
                  </Typography>
                  {integration.certificate?.fingerprint256 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .25, wordBreak: "break-all" }}>
                      Huella: {integration.certificate.fingerprint256}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Divider />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button type="submit" variant="contained" startIcon={<SaveRoundedIcon />} disabled={saving || testing || deleting} sx={{ textTransform: "none", fontWeight: 900 }}>
            {saving ? "Guardando..." : hasStoredCredentials ? "Guardar cambios" : "Guardar credenciales"}
          </Button>
          {hasStoredCredentials ? (
            <Button type="button" variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={validateStored} disabled={saving || testing || deleting} sx={{ textTransform: "none", fontWeight: 850 }}>
              {testing ? "Validando..." : "Validar de nuevo"}
            </Button>
          ) : null}
        </Stack>

        {hasStoredCredentials ? (
          <Button type="button" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={remove} disabled={saving || testing || deleting} sx={{ textTransform: "none", fontWeight: 850 }}>
            {deleting ? "Eliminando..." : "Eliminar configuración"}
          </Button>
        ) : null}
      </Stack>

      {integration.lastValidatedAt ? (
        <Typography variant="caption" color="text.secondary">
          Última validación local: {formatDate(integration.lastValidatedAt)}. La conexión WSAA/WSFE se incorpora en la siguiente capa de facturación.
        </Typography>
      ) : null}
    </Stack>
  );
}
